import { equalNumbers, result, sha256 } from "./core.js";
import type { VerificationCheck, VerificationResult } from "./core.js";

const TILES = 25;
const RTP_NUMERATOR = 98n;
const RTP_DENOMINATOR = 100n;

function multiplier(mines: number, picks: number): string {
  if (picks <= 0) return "1";
  let chance = 1;
  for (let index = 0; index < picks; index += 1) {
    chance *= (TILES - mines - index) / (TILES - index);
  }
  return (0.98 / chance).toFixed(8).replace(/0+$/, "").replace(/\.$/, "");
}

function payoutUnits(wagerUnits: number, mines: number, picks: number): number {
  let numerator = RTP_NUMERATOR;
  let denominator = RTP_DENOMINATOR;
  for (let index = 0; index < picks; index += 1) {
    numerator *= BigInt(TILES - index);
    denominator *= BigInt(TILES - mines - index);
  }
  const units = (BigInt(wagerUnits) * numerator) / denominator;
  return Number((units / 10n) * 10n);
}

export async function calculateMinesPositions(
  serverSeed: string,
  clientSeed: string,
  nonce: number,
  mines: number
): Promise<number[]> {
  if (!Number.isInteger(nonce) || nonce < 1) throw new Error("INVALID_NONCE");
  if (!Number.isInteger(mines) || mines < 1 || mines >= TILES) throw new Error("INVALID_MINES");
  const randomValues: number[] = [];
  for (let cursor = 0; randomValues.length < TILES - 1; cursor += 1) {
    const digest = await sha256(`${serverSeed}:${clientSeed}:${nonce}:${cursor}`);
    for (let offset = 0; offset < 64 && randomValues.length < TILES - 1; offset += 8) {
      randomValues.push(Number.parseInt(digest.slice(offset, offset + 8), 16) / 0x1_0000_0000);
    }
  }
  const cells = Array.from({ length: TILES }, (_, index) => index);
  for (let index = 0; index < TILES - 1; index += 1) {
    const selected = index + Math.floor(randomValues[index]! * (TILES - index));
    [cells[index], cells[selected]] = [cells[selected]!, cells[index]!];
  }
  return cells.slice(0, mines).sort((left, right) => left - right);
}

export async function verifyMines(input: {
  serverSeed: string;
  serverSeedHash: string;
  clientSeed: string;
  nonce: number;
  mines: number;
  recordedMinePositions: readonly number[];
  settlement?: {
    status: "won" | "lost";
    wagerUnits: number;
    picks: number;
    recordedMultiplier: string;
    recordedPayoutUnits: number;
  };
}): Promise<VerificationResult<{
  serverSeedHash: string;
  minePositions: number[];
  settlement: { multiplier: string; payoutUnits: number } | null;
}>> {
  if (input.settlement) {
    if (!Number.isSafeInteger(input.settlement.wagerUnits) || input.settlement.wagerUnits < 0) {
      throw new Error("INVALID_WAGER");
    }
    if (!Number.isInteger(input.settlement.picks)
      || input.settlement.picks < 0
      || input.settlement.picks > TILES - input.mines) {
      throw new Error("INVALID_PICKS");
    }
  }
  const calculatedHash = await sha256(input.serverSeed);
  const minePositions = await calculateMinesPositions(input.serverSeed, input.clientSeed, input.nonce, input.mines);
  const checks: VerificationCheck[] = [
    {
      key: "commitment",
      ok: calculatedHash === input.serverSeedHash,
      expected: input.serverSeedHash,
      actual: calculatedHash
    },
    {
      key: "outcome",
      ok: equalNumbers(minePositions, input.recordedMinePositions),
      expected: [...input.recordedMinePositions],
      actual: minePositions
    }
  ];
  let calculatedSettlement: { multiplier: string; payoutUnits: number } | null = null;
  if (input.settlement) {
    calculatedSettlement = input.settlement.status === "lost"
      ? { multiplier: "0", payoutUnits: 0 }
      : {
          multiplier: multiplier(input.mines, input.settlement.picks),
          payoutUnits: payoutUnits(input.settlement.wagerUnits, input.mines, input.settlement.picks)
        };
    checks.push({
      key: "payout",
      ok: calculatedSettlement.multiplier === input.settlement.recordedMultiplier
        && calculatedSettlement.payoutUnits === input.settlement.recordedPayoutUnits,
      expected: {
        multiplier: input.settlement.recordedMultiplier,
        payoutUnits: input.settlement.recordedPayoutUnits
      },
      actual: calculatedSettlement
    });
  }
  return result({ serverSeedHash: calculatedHash, minePositions, settlement: calculatedSettlement }, checks);
}
